/**
 * Set up a page.
 *
 * The panel opened with a chartreuse uppercase "PAGE SETUP" chip over a serif
 * headline naming the panel you were already looking at. Underneath, six
 * different outcomes were drawn in six different colours: accent for ready,
 * emerald for already-here, amber for add-the-domain-first, sky for
 * choose-which-one, accent again for out-of-room, and grey for a bad URL. A
 * person pastes a URL and gets one of six answers; the colour they get is not
 * one of the things they are trying to learn.
 *
 * Every outcome is a notice now, and only two of them are coloured: the one
 * that worked, and the one that needs something from you.
 */

import { FormEvent, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { apiService } from '@/services/api'
import { BILLING_PLANS_PATH } from '@/utils/freeTierPageSetup'
import { PAGE_SETUP_SUBSCRIPTION_REQUIRED_MESSAGE } from 'aeo-schema-generator-shared/utils'
import { usePageSetupAccess } from '@/hooks/usePageSetupAccess'
import AddDomainDialog from '@/components/domains/AddDomainDialog'
import { DsIsland } from '@/design-system/island'
import {
  Button,
  ButtonLink,
  Card,
  Field,
  Input,
  Notice,
} from '@/design-system/primitives'
import { Spinner } from '@/design-system/feedback'
import { Chip, Filters } from '@/design-system/data'
import type { PageSetupResolveResponse, UnifiedDomain } from '@shared/types'
import { useEngagementTracking } from '@/hooks/useEngagementTracking'
import '@/design-system/dashboard.css'
import '@/design-system/data.css'
import '@/design-system/form.css'
import '@/design-system/feedback.css'

/*
  This type used to live in DashboardCommandBar, a component that drew the
  search box and the filter chips and was never rendered anywhere: the same
  row was written a second time in the foot of this panel, and that is the one
  on screen. The component is gone; the type it was really exporting lives
  with the row that uses it.
*/
export type DashboardFeatureFilter = 'all' | 'scan' | 'bot-tracking' | 'schema' | 'drift'

interface DashboardPageSetupLauncherProps {
  domains: UnifiedDomain[]
  isLoadingDomains?: boolean
  canAddDomain: boolean
  canManageDomainRegistry: boolean
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  featureFilter: DashboardFeatureFilter
  onFeatureFilterChange: (value: DashboardFeatureFilter) => void
  featureFilters: { value: DashboardFeatureFilter; label: string; icon: React.ElementType }[]
  featureCounts: Record<DashboardFeatureFilter, number>
  filteredCount: number
  totalCount: number
  /*
    The add-a-site dialog is owned here, and the first-week guide on the
    dashboard needs to open the same one rather than draw a second. When the
    parent passes these two, it holds the open state; otherwise the launcher
    keeps its own.
  */
  addDomainOpen?: boolean
  onAddDomainOpenChange?: (open: boolean) => void
}

export function DashboardPageSetupLauncher({
  domains,
  isLoadingDomains = false,
  canAddDomain,
  canManageDomainRegistry,
  searchQuery,
  onSearchQueryChange,
  featureFilter,
  onFeatureFilterChange,
  featureFilters,
  featureCounts,
  filteredCount,
  totalCount,
  addDomainOpen,
  onAddDomainOpenChange,
}: DashboardPageSetupLauncherProps) {
  const [urlInput, setUrlInput] = useState('')
  const [result, setResult] = useState<PageSetupResolveResponse | null>(null)
  const [ownAddDomainOpen, setOwnAddDomainOpen] = useState(false)
  const showAddDomain = addDomainOpen ?? ownAddDomainOpen
  const setShowAddDomain = (open: boolean) => {
    setOwnAddDomainOpen(open)
    onAddDomainOpenChange?.(open)
  }
  const trackEngagement = useEngagementTracking()
  const hasDomains = domains.length > 0

  /**
   * Generation is a subscription feature, so offering "Start setup" to a free
   * account sends them one click further just to be refused, spending the one
   * moment they were motivated on a dead end. Only new pages are affected: a
   * page already in their library stays regenerable on any plan.
   */
  const { needsSubscription: needsSubscriptionForNewPage } = usePageSetupAccess()

  const resolveMutation = useMutation({
    mutationFn: (url: string) => apiService.resolvePageSetupUrl(url),
    onSuccess: (response) => setResult(response.data),
    onError: () => {
      setResult({
        status: 'invalid_url',
        input: urlInput,
        message: 'We could not check that URL. Please try again.',
      })
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmed = urlInput.trim()
    if (!trimmed || resolveMutation.isPending) return
    setResult(null)
    trackEngagement('page_setup_launcher_used', {
      inputKind: trimmed.startsWith('/') ? 'path' : trimmed.includes('://') ? 'url' : 'domain_or_path',
    })
    resolveMutation.mutate(trimmed)
  }

  const renderResult = () => {
    if (!result) return null

    if (result.status === 'ready_new') {
      const displayUrl = result.canonicalUrl || result.normalizedUrl
      const redirected = !!result.canonicalUrl && result.canonicalUrl !== result.input

      if (needsSubscriptionForNewPage) {
        return (
          <div data-testid="page-setup-subscription-required" className="ds-dash__answer">
            <Notice>
              <b>Setting up a new page comes with a plan</b>
              <p>
                {PAGE_SETUP_SUBSCRIPTION_REQUIRED_MESSAGE} We checked {displayUrl} and it is ready
                to go.
              </p>
              <ButtonLink to={BILLING_PLANS_PATH} variant="secondary" size="sm">
                See the plans
                <ArrowRight aria-hidden="true" />
              </ButtonLink>
            </Notice>
          </div>
        )
      }

      return (
        <div className="ds-dash__answer">
          <Notice kind="good">
            <b>Ready to set up</b>
            <p>{displayUrl}</p>
            {redirected && result.canonicalHost && (
              <p>
                That URL redirects, so we will use {result.canonicalHost} as the page's real
                address.
              </p>
            )}
            {result.setupUrl && (
              <ButtonLink to={result.setupUrl} size="sm">
                Start setting it up
                <ArrowRight aria-hidden="true" />
              </ButtonLink>
            )}
          </Notice>
        </div>
      )
    }

    if (result.status === 'matched_existing') {
      return (
        <div className="ds-dash__answer">
          <Notice kind="good">
            <b>You already have this page</b>
            <p>{result.existingPage?.message || 'It is already in this workspace.'}</p>
            {result.pageDetailUrl && (
              <ButtonLink to={result.pageDetailUrl} variant="secondary" size="sm">
                Open it
                <ArrowRight aria-hidden="true" />
              </ButtonLink>
            )}
          </Notice>
        </div>
      )
    }

    if (result.status === 'domain_not_found') {
      return (
        <div className="ds-dash__answer">
          <Notice kind="look">
            <b>That site is not in this workspace yet</b>
            <p>{result.message || `${result.suggestedDomain} is not in this workspace yet.`}</p>
            {canAddDomain && (
              <Button size="sm" onClick={() => setShowAddDomain(true)}>
                Add it
                <ArrowRight aria-hidden="true" />
              </Button>
            )}
          </Notice>
        </div>
      )
    }

    if (result.status === 'ambiguous_match') {
      return (
        <div className="ds-dash__answer">
          <Notice>
            <b>That path is on more than one of your sites</b>
            <p>{result.message || 'Pick the one you meant.'}</p>
            <ul className="ds-dash__picks">
              {(result.candidates || []).map((candidate) => (
                <li key={`${candidate.url}-${candidate.pageDetailUrl || ''}`}>
                  <span className="ds-dash__pick">
                    <b>{candidate.host}</b>
                    <span className="ds-mono">{candidate.url}</span>
                  </span>
                  <ButtonLink to={candidate.pageDetailUrl || '#'} variant="secondary" size="sm">
                    Set up this page
                  </ButtonLink>
                </li>
              ))}
            </ul>
          </Notice>
        </div>
      )
    }

    if (result.status === 'domain_limit_reached') {
      return (
        <div className="ds-dash__answer">
          <Notice kind="look">
            <b>Your plan is out of room for another site</b>
            <p>{result.message || 'A larger plan makes room for more.'}</p>
            <ButtonLink to={BILLING_PLANS_PATH} variant="secondary" size="sm">
              See the plans
              <ArrowRight aria-hidden="true" />
            </ButtonLink>
          </Notice>
        </div>
      )
    }

    return (
      <div className="ds-dash__answer">
        <Notice kind="look">
          <b>{result.status === 'permission_required' ? 'Ask a workspace admin' : 'Check the address'}</b>
          <p>{result.message || 'Paste a full page address from one of your sites.'}</p>
        </Notice>
      </div>
    )
  }

  return (
    <DsIsland panel={false} ground={false}>
      <div data-testid="dashboard-page-setup-launcher">
        <Card
          title="Set up a page"
          foot={
            hasDomains ? (
              <Filters>
                <div className="ds-filters__search">
                  <Input
                    value={searchQuery}
                    onChange={(event) => onSearchQueryChange(event.target.value)}
                    placeholder="Search your sites"
                    aria-label="Search your sites"
                  />
                </div>
                {featureFilters.map((filter) => {
                  const count = featureCounts[filter.value]
                  if (filter.value === 'drift' && count === 0) return null
                  return (
                    <Chip
                      key={filter.value}
                      count={count}
                      on={featureFilter === filter.value}
                      onClick={() => onFeatureFilterChange(filter.value)}
                    >
                      {filter.label}
                    </Chip>
                  )
                })}
                <span className="ds-dash__showing">
                  Showing {filteredCount.toLocaleString()} of {totalCount.toLocaleString()}
                </span>
              </Filters>
            ) : undefined
          }
        >
          <p className="ds-lead ds-dash__setuplead">
            Paste a page from one of your sites and we will write its markup and its AI-readable
            copy. If we already have it, we will take you straight there.
          </p>

          {isLoadingDomains ? (
            <div className="ds-dash__wait" aria-live="polite" aria-busy="true">
              <Spinner quiet />
              <span>Looking up your sites</span>
            </div>
          ) : hasDomains ? (
            <form onSubmit={handleSubmit} className="ds-dash__setup">
              <Field label="Page address">
                {(id) => (
                  <Input
                    id={id}
                    value={urlInput}
                    onChange={(event) => {
                      setUrlInput(event.target.value)
                      if (result) setResult(null)
                    }}
                    placeholder={domains.length === 1 ? '/pricing' : 'https://example.com/pricing'}
                  />
                )}
              </Field>
              <Button type="submit" disabled={!urlInput.trim()} loading={resolveMutation.isPending}>
                {resolveMutation.isPending ? 'Checking that page' : 'Start Page Setup'}
              </Button>
            </form>
          ) : (
            <div className="ds-dash__setup">
              <p className="ds-dash__setuplead">
                Page Setup starts with a site, so what we write stays attached to the right one.
              </p>
              {canAddDomain && canManageDomainRegistry && (
                <Button onClick={() => setShowAddDomain(true)}>
                  Add your first site
                  <ArrowRight aria-hidden="true" />
                </Button>
              )}
            </div>
          )}

          {renderResult()}
        </Card>

        {showAddDomain && (
          <AddDomainDialog isOpen={showAddDomain} onClose={() => setShowAddDomain(false)} />
        )}
      </div>
    </DsIsland>
  )
}
